import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Package, Radio, ShoppingBag, DollarSign, Wand2, Camera, Plus, Video, Bot, Share2, Puzzle, User } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function SellerDashboard() {
  const { user, saveOnboarding } = useAuth();
  const navigate = useNavigate();
  const { data: stats } = useQuery({
    queryKey: ["seller-stats"],
    queryFn: async () => (await api.get("/seller/stats")).data,
  });

  const cards = [
    { icon: Package, label: "Products", value: stats?.product_count ?? "—" },
    { icon: Radio, label: "Live", value: stats?.live_count ?? "—" },
    { icon: ShoppingBag, label: "Orders", value: stats?.order_count ?? "—" },
    { icon: DollarSign, label: "Revenue", value: stats ? `$${stats.revenue}` : "—" },
  ];

  const actions = [
    { icon: Wand2, title: "Closet ingest", desc: "Drop photos. AI writes the catalog. Save as drafts, then studio, then publish.", to: "/seller/add" },
    { icon: Camera, title: "AI studio", desc: "Turn a flat lay into a model shot, attach it to the draft, then publish when you are ready.", to: "/seller/photoshoot" },
    { icon: Video, title: "Go Live", desc: "Broadcast in real time and sell as you stream.", to: "/seller/live" },
    { icon: Package, title: "Manage Inventory", desc: "Edit stock, shipping rates, and go live with your pieces.", to: "/seller/products" },
    { icon: Share2, title: "Crosslist", desc: "Create item, inventory, labels, import, analytics, templates, export CSV.", to: "/seller/crosslist" },
    { icon: Puzzle, title: "Marketplaces", desc: "Connect eBay, Etsy, Depop, Amazon, Shopify, Flipkart, Poshmark, and the other helper shops from Connections. Official APIs post for you; helper shops copy a pack and you click List on their site.", to: "/seller/connections" },
    { icon: User, title: "Profile & shipping", desc: "Shop address and ship-from ZIP. Etsy needs a postal code before Post can create a shipping profile.", to: "/profile" },
    { icon: Wand2, title: "Automations", desc: "Send offers, auto offers, sharing reminders, stale listing warning.", to: "/seller/automations" },
    { icon: ShoppingBag, title: "Orders to ship", desc: "See buyer addresses and fulfill paid orders.", to: "/seller/orders" },
    { icon: Bot, title: "AI shopping apps", desc: "Show your listings in shopping assistants, set their commission, and see what shoppers asked.", to: "/seller/ai-apps" },
  ];

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-28 pb-24">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-2">Seller Studio</div>
          <h1 className="text-3xl font-semibold">{user?.profile?.onboarding?.shop_name || user?.name}</h1>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/seller/connections" className="px-4 py-2.5 border border-aura-border text-sm rounded-lg" data-testid="dashboard-connect">
            Connect shops
          </Link>
          <Link to="/profile" className="px-4 py-2.5 border border-aura-border text-sm rounded-lg" data-testid="dashboard-profile">
            Profile & shipping
          </Link>
          <button
            type="button"
            onClick={async () => {
              try {
                await saveOnboarding({ done: false, step: "welcome" });
              } catch { /* still open the wizard */ }
              navigate("/seller/onboarding");
            }}
            className="px-4 py-2.5 border border-aura-border text-sm rounded-lg"
            data-testid="replay-onboarding"
          >
            Shop setup
          </button>
          <Link to="/seller/add" className="px-5 py-2.5 bg-aura-accent text-white text-sm flex items-center gap-2 rounded-lg hover:opacity-90 transition-opacity" data-testid="new-listing-btn">
            <Plus className="w-4 h-4" /> New listing
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        {cards.map((c) => (
          <div key={c.label} className="bg-aura-surface border border-aura-border rounded-lg p-6 hover:shadow-sm transition-shadow" data-testid={`stat-${c.label.toLowerCase()}`}>
            <c.icon className="w-5 h-5 text-aura-accent mb-4" strokeWidth={1.5} />
            <div className="text-3xl font-semibold font-mono">{c.value}</div>
            <div className="text-xs text-aura-muted mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {actions.map((a) => (
          <Link key={a.to} to={a.to} className="group bg-aura-surface border border-aura-border rounded-lg p-6 hover:shadow-sm hover:border-aura-accent transition-all" data-testid={`action-${a.to.split("/").pop()}`}>
            <a.icon className="w-6 h-6 text-aura-ink mb-4" strokeWidth={1.5} />
            <div className="font-medium mb-1">{a.title}</div>
            <div className="text-sm text-aura-muted leading-relaxed">{a.desc}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}
